import { useState, useEffect } from "react";
import type { MomentButton } from "../../data/momentOptions";
import { SectionBox, inputCls } from "../shared";

type Props = {
  buttons: MomentButton[];
  maxButtons: number;
  buttonsEnabled: boolean;
  onButtonsEnabledChange: (val: boolean) => void;
  onButtonChange: (id: string, key: keyof MomentButton, value: string) => void;
  onAddButton: () => void;
  onRemoveButton: (id: string) => void;
};

export default function ButtonSection({
  buttons, maxButtons, buttonsEnabled,
  onButtonsEnabledChange, onButtonChange, onAddButton, onRemoveButton,
}: Props) {
  const [buttonPcEnabled, setButtonPcEnabled] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setButtonPcEnabled(
      Object.fromEntries(buttons.map((button) => [button.id, button.pcLandingUrl.trim().length > 0]))
    );
  }, [buttons]);

  const handleButtonPcToggle = (id: string, checked: boolean) => {
    setButtonPcEnabled((prev) => ({ ...prev, [id]: checked }));
    if (!checked) onButtonChange(id, "pcLandingUrl", "");
  };

  // 현재 유형의 maxButtons에 맞게 표시할 버튼만 slice
  const visibleButtons = buttons.slice(0, maxButtons);

  return (
    <SectionBox label="버튼" enabled={buttonsEnabled} onToggle={onButtonsEnabledChange}>
      <div className="tw-space-y-2">
        <div className="tw-flex tw-justify-between tw-items-center">
          <span className="tw-text-xs tw-text-gray-500">최대 {maxButtons}개</span>
          <button
            type="button"
            onClick={onAddButton}
            disabled={buttons.length >= maxButtons}
            className="tw-text-xs tw-bg-blue-50 tw-text-blue-600 tw-px-3 tw-py-1 tw-rounded-lg tw-border tw-border-blue-200 disabled:tw-opacity-40"
          >
            + 추가
          </button>
        </div>
        {visibleButtons.map((button, index) => (
          <div key={button.id} className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-space-y-2">
            <div className="tw-flex tw-justify-between tw-items-center">
              <span className="tw-text-xs tw-font-semibold tw-text-gray-600">버튼 {index + 1}</span>
              <button
                type="button"
                onClick={() => onRemoveButton(button.id)}
                className="tw-text-xs tw-bg-red-50 tw-text-red-400 tw-px-3 tw-py-1 tw-rounded-lg tw-border tw-border-red-200"
              >
                삭제
              </button>
            </div>
            <input
              value={button.title}
              onChange={(e) => onButtonChange(button.id, "title", e.target.value.slice(0, 8))}
              placeholder="버튼명 (최대 8자)"
              maxLength={8}
              className={inputCls}
            />
            <div className="tw-flex tw-justify-end tw-text-xs tw-text-gray-400">
              <span>{button.title.length} / 8</span>
            </div>
            <div>
              <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1.5">
                <span className="tw-text-red-500 tw-mr-1">*</span>
                모바일 랜딩 URL
              </label>
              <input
                value={button.mobileLandingUrl}
                onChange={(e) => onButtonChange(button.id, "mobileLandingUrl", e.target.value)}
                placeholder="https://"
                className={inputCls}
              />
            </div>
            <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 tw-cursor-pointer tw-w-fit">
              <input
                type="checkbox"
                checked={buttonPcEnabled[button.id] ?? false}
                onChange={(e) => handleButtonPcToggle(button.id, e.target.checked)}
                className="tw-w-3.5 tw-h-3.5"
              />
              PC 링크
            </label>
            {buttonPcEnabled[button.id] && (
              <div>
                <label className="tw-block tw-text-xs tw-text-gray-500 tw-mb-1.5">PC 랜딩 URL</label>
                <input
                  value={button.pcLandingUrl}
                  onChange={(e) => onButtonChange(button.id, "pcLandingUrl", e.target.value)}
                  placeholder="https://"
                  className={inputCls}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </SectionBox>
  );
}
